import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'

import { FormConfig } from '@workwave-tidal/core/form'

import { useResidentModalsApi } from '@/components/modals/ResidentModals'
import {
  computeOrdersNumberAfterRecurrenceUpsert,
  selectStorageSpaceData,
} from '@/features/domain/order'
import { useNotification } from '@/hooks'
import { useNavigate } from '@/routing'
import { isDeepEqual } from '@/server-data'
import { useAppDispatch } from '@/store'

import * as OrderTexts from '../../../intl'
import { form } from './formState'
import type { OrderFormValues, Props } from './typings'

import {
  getCreateOrderFromFormValues,
  getFormInitialValues,
  getOrderStepUpdateChanges,
  isCreatingOrder,
} from './utils/getOrderStepData'
import { computeAutomaticRange } from './utils/computeAutomaticRange'
import { computeDirtyFields } from './utils/computeDirtyFields'
import { computeRecurrenceUpdateRequest } from './utils/computeRecurrenceUpdateRequest'
import { getNewTargetRanges } from './utils/getNewTargetRanges'

import { Footer } from './components/Footer'
import { Form } from './components/Form'
import { Header } from './components/Header'
import { useCollectRecurrenceTargetRangeModal } from './components/modals/CollectRecurrenceTargetRangeModal'
import { useConfirmChangeFromRecurrenceToRegularOrderModal } from './components/modals/ConfirmChangeFromRecurrenceToRegularOrderModal'

type TargetRange = 'ORDER' | 'FUTURE' | 'ALL'

export function EditOrderStep(props: Props) {
  const {
    tab,
    order,
    orders,
    depots,
    mapCenter,
    companies,
    planLocked,
    recurrences,
    stopEditing,
    createOrder,
    maxTagsCount,
    updateOrders,
    removeInspectPin,
    workingDayStartSec,
    initialLatLngFromInspectPin,
  } = props

  const [initialValues] = useState(() =>
    getFormInitialValues(
      mapCenter,
      orders,
      workingDayStartSec,
      depots,
      companies,
      order,
      initialLatLngFromInspectPin,
      recurrences,
    ),
  )

  const collectTargetRangePromiseRef = useRef<{
    resolve: (value: TargetRange) => void
    reject: (reason?: any) => void
  }>()

  const confirmFromRecurrenceToSingleOrderPromiseRef = useRef<{
    resolve: () => void
    reject: (reason?: any) => void
  }>()

  const unableToCreateRecurrenceOrderPromiseRef = useRef<{
    resolve: () => void
    reject: (reason?: any) => void
  }>()

  const { percentage: storagePercentage } = useSelector(selectStorageSpaceData)
  const {
    unableToCreateRecurrentOrders: { show: showUnableToCreateRecurrenceOrderModal },
  } = useResidentModalsApi()

  const navigate = useNavigate<uui.routing.Setup>(true)
  const [allowedTargetRanges, setAllowedTargetRanges] =
    useState<uui.domain.ui.forms.RecurrenceCollectedTargetRange>({
      ORDER: { visible: true, disabled: false },
      FUTURE: { visible: true, disabled: false },
      ALL: { visible: true, disabled: false },
    })

  const dispatch = useAppDispatch()
  const toast = useNotification()

  const { show: showCollectRecurrenceTargetModal, Modal: CollectRecurrenceTargetRangeModal } =
    useCollectRecurrenceTargetRangeModal()

  const { show: showConfirmChangeModal, Modal: ConfirmChangeFromRecurrenceToRegularOrderModal } =
    useConfirmChangeFromRecurrenceToRegularOrderModal()

  const collectTargetRange = useCallback(async (): Promise<TargetRange> => {
    return new Promise<TargetRange>((resolve, reject) => {
      collectTargetRangePromiseRef.current = { resolve, reject }
      showCollectRecurrenceTargetModal()
    })
  }, [showCollectRecurrenceTargetModal])

  const confirmFromRecurrenceToSingleOrder = useCallback(async () => {
    return new Promise<void>((resolve, reject) => {
      confirmFromRecurrenceToSingleOrderPromiseRef.current = { resolve, reject }
      showConfirmChangeModal()
    })
  }, [showConfirmChangeModal])

  const alertUserUnableToCreateRecurrenceOrder = useCallback(() => {
    return new Promise<void>((resolve, reject) => {
      unableToCreateRecurrenceOrderPromiseRef.current = { resolve, reject }
      showUnableToCreateRecurrenceOrderModal({
        mode: 'create',
        onManageStorage: () => {
          if (unableToCreateRecurrenceOrderPromiseRef.current) {
            unableToCreateRecurrenceOrderPromiseRef.current.resolve()
            unableToCreateRecurrenceOrderPromiseRef.current = undefined
          }
        },
        onGoBackToEdit: () => {
          if (unableToCreateRecurrenceOrderPromiseRef.current) {
            unableToCreateRecurrenceOrderPromiseRef.current.reject()
            unableToCreateRecurrenceOrderPromiseRef.current = undefined
          }
        },
        storageSaturationPercent: storagePercentage,
      })
    })
  }, [showUnableToCreateRecurrenceOrderModal])

  async function checkCanAddOrders(
    orderRequest: uui.domain.ui.forms.OrderCreate | uui.domain.ui.forms.OrderUpdate,
  ) {
    const checkCanAddOrdersRequest = await dispatch(
      computeOrdersNumberAfterRecurrenceUpsert([orderRequest]),
    )

    if (computeOrdersNumberAfterRecurrenceUpsert.fulfilled.match(checkCanAddOrdersRequest)) {
      return checkCanAddOrdersRequest.payload.canProceed
    }
  }

  async function alertUserNotAbleToCreateOrders() {
    await alertUserUnableToCreateRecurrenceOrder()
    navigate('territory')
  }

  // Callback to handle form submit
  const handleOnSubmit = useCallback(
    async (updatedOrder: OrderFormValues) => {
      try {
        if (isCreatingOrder(initialValues)) {
          const orderCreate = getCreateOrderFromFormValues(updatedOrder, workingDayStartSec)
          const canAddOrders = await checkCanAddOrders(orderCreate)
          if (!canAddOrders) {
            await alertUserNotAbleToCreateOrders()
            return
          }
          await createOrder(orderCreate)
          return
        }

        const wasRecurrence = initialValues.order.recurrenceId
        const isRecurrence =
          updatedOrder.recurrenceSettings.recurrencePeriod.recurrenceType !== 'NO_REPEAT'

        const neverBeenARecurrentOrder = !wasRecurrence && !isRecurrence

        const dirtyFields = computeDirtyFields(initialValues, updatedOrder)

        if (dirtyFields.length === 0) {
          stopEditing()
          return
        }

        const orderUpdateChanges = getOrderStepUpdateChanges(
          updatedOrder,
          dirtyFields,
          initialValues,
          workingDayStartSec,
        )

        if (neverBeenARecurrentOrder) {
          // Never dispatch an empty update: the server would produce no change set and
          // the update saga blocks forever on `waitForChangeDataSuccess`, hanging the
          // save spinner. Close the editor as if pristine instead. Scoped to the
          // single-order path only — the recurrence path below also carries recurrence
          // changes (updateRecurrence / NO_REPEAT→repeat conversion) that aren't
          // reflected in orderUpdateChanges, so it must not be short-circuited here.
          const hasChanges =
            !!orderUpdateChanges.add ||
            !!orderUpdateChanges.delete ||
            Object.keys(orderUpdateChanges.update ?? {}).length > 0

          if (!hasChanges) {
            stopEditing()
            return
          }

          const orderUpdate = {
            type: 'single-order',
            orderUpdateChanges,
          } as const

          const canAddOrders = await checkCanAddOrders(orderUpdate)

          if (!canAddOrders) {
            await alertUserNotAbleToCreateOrders()
            return
          }

          await updateOrders(orderUpdate)
          return
        }

        const automaticRange = computeAutomaticRange({
          initialValues,
          currentValues: updatedOrder,
        })

        if (automaticRange === 'NOT_REPEAT') {
          await confirmFromRecurrenceToSingleOrder()
        }

        const eligibilityUpdated =
          updatedOrder.recurrenceSettings.recurrencePeriod.date !==
          initialValues.recurrenceSettings.recurrencePeriod.date

        const { recurrenceSettings: initialRecurrenceSettings, ...initialOrderSettings } =
          initialValues
        const { recurrenceSettings: updatedRecurrenceSettings, ...updatedOrderSettings } =
          updatedOrder
        const orderUpdated = !isDeepEqual(initialOrderSettings, updatedOrderSettings)

        const updateRecurrence = computeRecurrenceUpdateRequest(updatedOrder)
        const recurrenceUpdated = !isDeepEqual(
          computeRecurrenceUpdateRequest(initialValues),
          updateRecurrence,
        )

        const lastOrderOfRecurrence = updatedOrder.order?.nextRecurrentOrder === null
        const moreOrdersWillBeGenerated =
          recurrences && updatedOrder.order.recurrenceId
            ? recurrences[updatedOrder.order.recurrenceId]?.nextRecurrentOrderDate != null
            : false

        const newTargetRanges = getNewTargetRanges(
          eligibilityUpdated,
          recurrenceUpdated,
          orderUpdated,
          lastOrderOfRecurrence,
          moreOrdersWillBeGenerated,
          OrderTexts,
        )

        setAllowedTargetRanges(newTargetRanges)

        const collectedTargetRange = automaticRange ?? (await collectTargetRange())

        const dateChanged =
          initialValues.recurrenceSettings.recurrencePeriod.date !==
          updatedOrder.recurrenceSettings.recurrencePeriod.date

        const isNotNoRepeat =
          updatedOrder.recurrenceSettings.recurrencePeriod.recurrenceType !== 'NO_REPEAT'

        if (dateChanged && collectedTargetRange !== 'NOT_REPEAT' && isNotNoRepeat) {
          if (!orderUpdateChanges.update) {
            orderUpdateChanges.update = {}
          }
          if (!orderUpdateChanges.update.order) {
            orderUpdateChanges.update.order = {}
          }

          orderUpdateChanges.update.order.eligibility = {
            type: 'on',
            dates: [updatedOrder.recurrenceSettings.recurrencePeriod.date],
          }
        }

        const orderUpdate = {
          type: 'recurrence',
          orderUpdateChanges,
          updateRecurrence,
          targetRange: collectedTargetRange,
          startDate: updatedOrder.recurrenceSettings.recurrencePeriod.date,
        } as const

        const canAddOrders = await checkCanAddOrders(orderUpdate)

        if (!canAddOrders) {
          await alertUserNotAbleToCreateOrders()
          return
        }

        await updateOrders(orderUpdate)
      } catch (error) {
        if (error instanceof Error) {
          toast.error(error.message)
        }
      }
    },
    [initialValues, order, createOrder, workingDayStartSec, updateOrders, toast, stopEditing],
  )

  // Callback to handle form cancel
  const handleOnCancel = useCallback(() => {
    stopEditing()
  }, [stopEditing])

  // Remove inspect pin if it was used to set initial lat lng
  useEffect(() => {
    if (initialLatLngFromInspectPin) {
      removeInspectPin()
    }
  }, [initialLatLngFromInspectPin, removeInspectPin, stopEditing])

  const config = useMemo<FormConfig<OrderFormValues>>(
    () => ({
      id: 'order-form',
      initialValues: initialValues,
    }),
    [initialValues],
  )

  return (
    <>
      <form.Form
        header={
          <Header initialValues={initialValues} order={order} tab={tab} onClose={handleOnCancel} />
        }
        footer={
          <Footer
            planLocked={planLocked}
            creating={isCreatingOrder(initialValues)}
            handleCancel={handleOnCancel}
          />
        }
        onSubmit={handleOnSubmit}
        config={config}
        rightBorder
        requireTransaction
        stopEditingOnUnmount
        applyPendingFitMapOnMount={true}
        disableScroll
      >
        <Form {...props} initialValues={initialValues} maxTagsCount={maxTagsCount} />
      </form.Form>

      <CollectRecurrenceTargetRangeModal
        allowedTargetRanges={allowedTargetRanges}
        collectTargetRange={targetRange => {
          if (collectTargetRangePromiseRef.current) {
            collectTargetRangePromiseRef.current.resolve(targetRange)
            collectTargetRangePromiseRef.current = undefined
          }
        }}
        onCancel={() => {
          if (collectTargetRangePromiseRef.current) {
            collectTargetRangePromiseRef.current.reject()
            collectTargetRangePromiseRef.current = undefined
          }
        }}
      />
      <ConfirmChangeFromRecurrenceToRegularOrderModal
        onConfirm={() => {
          if (confirmFromRecurrenceToSingleOrderPromiseRef.current) {
            confirmFromRecurrenceToSingleOrderPromiseRef.current.resolve()
            confirmFromRecurrenceToSingleOrderPromiseRef.current = undefined
          }
        }}
        onCancel={() => {
          if (confirmFromRecurrenceToSingleOrderPromiseRef.current) {
            confirmFromRecurrenceToSingleOrderPromiseRef.current.reject()
            confirmFromRecurrenceToSingleOrderPromiseRef.current = undefined
          }
        }}
      />
    </>
  )
}
